import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sprout, TreePine, Home as HomeIcon, Building, Flower2, Wheat, Building2, Briefcase, School, Truck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import Seo from "@/components/Seo";
import { SERVICE_TYPES, SERVICE_TYPE_BY_SLUG } from "./serviceTypes";
import { useServiceRequestForm } from "./useServiceRequestForm";
import ProgressBar from "./ProgressBar";
import Sidebar from "./Sidebar";
import SuccessScreen from "./SuccessScreen";
import ContactDetails from "./Steps/ContactDetails";
import LocationSchedule from "./Steps/LocationSchedule";
import SpaceDetails from "./Steps/SpaceDetails";
import Review from "./Steps/Review";
import ConsultationPayment from "./Steps/ConsultationPayment";

const TYPE_ICONS = {
  Sprout, TreePine, Home: HomeIcon, Building, Flower2, Wheat, Building2, Briefcase, School, Truck,
};

const STEP_COUNT = 5;

function validateStep(step, form, selectedType) {
  if (step === 0) {
    if (!selectedType) return "Please select a visit type above.";
    if (!form.full_name || !form.phone || !form.customer_type) return "Please complete all required fields.";
    if (!/^[6-9]\d{9}$/.test(form.phone)) return "Enter a valid 10-digit mobile number.";
    return null;
  }
  if (step === 1) {
    if (!form.address || !form.pincode || !form.city || !form.state || !form.date || !form.time) return "Please complete all required fields.";
    if (!/^\d{6}$/.test(form.pincode)) return "Enter a valid 6-digit PIN code.";
    return null;
  }
  if (step === 2) {
    if (!form.property_type || !form.description) return "Please complete all required fields.";
    if (form.needs.length === 0) return "Select at least one requirement.";
    if (selectedType?.extraFields === "nursery" && !form.selected_nursery) return "Please select a nursery to visit.";
    return null;
  }
  if (step === 3) {
    if (!form.consent) return "Please provide consent to submit.";
    return null;
  }
  return null;
}

export default function ServiceRequest() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const initialSlug = sp.get("type") || "";
  const [selectedSlug, setSelectedSlug] = useState(SERVICE_TYPE_BY_SLUG[initialSlug] ? initialSlug : "");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const pin = localStorage.getItem("yp_pin") || "";
  const { form, upd, updExtra, toggleNeed } = useServiceRequestForm(user, pin);

  const selectedType = useMemo(() => SERVICE_TYPE_BY_SLUG[selectedSlug], [selectedSlug]);

  const next = () => {
    const error = validateStep(step, form, selectedType);
    if (error) return toast.error(error);
    setStep((s) => Math.min(STEP_COUNT - 1, s + 1));
    window.scrollTo({ top: document.getElementById("service-request-form")?.offsetTop - 20 || 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const buildPayload = () => ({ ...form, visit_type: selectedType.label });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const data = await api.serviceRequests.create(buildPayload());
      setConfirmed(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    setSubmitting(true);
    try {
      const data = await api.serviceRequests.create(buildPayload());
      if (data.razorpay) {
        await payWithRazorpay({
          razorpay: data.razorpay,
          description: `${selectedType.label} consultation`,
          prefill: { name: form.full_name, email: form.email, contact: form.phone },
          verify: async (body) => {
            await api.serviceRequests.verifyPayment(data.id, body);
            setConfirmed(data);
            toast.success("Payment successful");
          },
          onDismiss: () => setSubmitting(false),
          onFail: (msg) => { toast.error(msg); setSubmitting(false); },
        });
      } else {
        // Razorpay not configured server-side — the request is already simulated-paid and final.
        setConfirmed(data);
      }
    } catch (e) {
      toast.error(e.message);
      setSubmitting(false);
    }
  };

  const reset = () => window.location.reload();

  if (confirmed) return <SuccessScreen request={confirmed} onReset={reset} />;

  return (
    <div id="service-request-form" data-testid="service-request-page" className="mx-auto max-w-4xl px-4 py-10 md:py-14">
      <Seo
        title="Book a Site Visit or Consultation"
        description="Book a nursery visit, landscaping site visit or plant consultation with YourPlants.in — free first consultation, expert horticulturists, tailored plant plans."
        path="/site-visit"
      />
      <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Site Visit &amp; Green Consultation</div>
      <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--yp-primary)]">How can YourPlants help?</h1>
      <p className="text-[var(--yp-muted)] mt-2 max-w-2xl">Select the closest option. The form below adapts to your project.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6">
        {SERVICE_TYPES.map((t) => {
          const Icon = TYPE_ICONS[t.icon];
          return (
            <button
              key={t.slug}
              type="button"
              data-testid={`visit-type-${t.slug}`}
              onClick={() => setSelectedSlug(t.slug)}
              className={`text-left p-4 rounded-2xl border transition-colors ${selectedSlug === t.slug ? "border-[var(--yp-primary)] bg-[var(--yp-surface-alt)]" : "border-[var(--yp-border)] hover:border-[var(--yp-primary)]"}`}
            >
              <Icon size={20} className="text-[var(--yp-primary)]" />
              <div className="text-sm font-semibold mt-2">{t.label}</div>
              <div className="text-xs text-[var(--yp-muted)] mt-1">{t.description}</div>
            </button>
          );
        })}
      </div>

      <ProgressBar step={step} />

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-6 items-start">
        <div className="bg-white border border-[var(--yp-border)] rounded-2xl p-6">
          {step === 0 && <ContactDetails form={form} upd={upd} />}
          {step === 1 && <LocationSchedule form={form} upd={upd} />}
          {step === 2 && <SpaceDetails form={form} upd={upd} updExtra={updExtra} toggleNeed={toggleNeed} extraFieldsKey={selectedType?.extraFields || "generic"} />}
          {step === 3 && <Review form={form} selectedType={selectedType} upd={upd} />}
          {step === 4 && <ConsultationPayment form={form} upd={upd} onPay={handlePay} submitting={submitting} />}

          <div className="mt-6 flex justify-between">
            <button type="button" onClick={back} className={`text-sm text-[var(--yp-muted)] hover:text-[var(--yp-primary)] ${step === 0 ? "invisible" : ""}`}>← Back</button>
            {step < STEP_COUNT - 1 ? (
              <button type="button" data-testid="sr-next" onClick={next} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium">Continue →</button>
            ) : form.consultation_type === "first" ? (
              <button type="button" data-testid="sr-submit" onClick={handleSubmit} disabled={submitting} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-70">
                {submitting ? "Submitting…" : "Submit Site Visit Request"}
              </button>
            ) : null}
          </div>
        </div>

        <Sidebar selectedType={selectedType} form={form} />
      </div>
    </div>
  );
}
